

import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { environment } from './enviroment';

// Request configuration type
export type RequestOptions = {
  path: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  data?: unknown;
  headers?: Record<string, string>;
  params?: Record<string, unknown>;
  timeout?: number;
};

// Custom error class for API errors
export class ApiError extends Error {
  public status?: number;
  public code?: string;
  public details?: unknown;

  constructor(message: string, status?: number, code?: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

// Dispatched on `window` when the server tells us the stored token is dead. AuthContext
// listens and tears the session down in memory — no reload, no navigation.
export const SESSION_EXPIRED_EVENT = 'yetfarrag:session-expired';

// Is this request the session-validation call (GET /auth/me)?
//
// Path-suffix match after stripping the query string and trailing slashes — deliberately NOT
// `includes('/auth/me')`, which would also match `/auth/mesh` and any URL that merely carries
// the string in a query parameter.
export const isSessionValidationRequest = (url?: string): boolean => {
  if (!url) return false;
  const path = url.split('?')[0].split('#')[0].replace(/\/+$/, '');
  if (!path) return false;
  return path === '/auth/me' || path === 'auth/me' || path.endsWith('/auth/me');
};

// Create axios instance with base configuration
const createAxiosInstance = () => {
  const instance = axios.create({
    baseURL: environment.baseUrl,
    timeout: 10000, // 10 seconds timeout
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Response interceptor for consistent error handling
  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => {
      // React ONLY to a 401 from the session-validation endpoint (only on client).
      //
      // `requestWithAuth` is the shared transport for reviewService, feedService,
      // dashboardService, leaderboardService and adminService, so the old blanket
      // `status === 401` branch let a per-resource 401 or a permission 401 from any of
      // those delete the token and destroy the whole session. Only /auth/me answering 401
      // actually means "this token is dead".
      //
      // There is deliberately no `window.location.href` here: a mid-session full reload is
      // exactly what users perceive as being randomly signed out. AuthContext handles the
      // state change in-app via SESSION_EXPIRED_EVENT.
      if (
        typeof window !== 'undefined' &&
        error.response?.status === 401 &&
        isSessionValidationRequest(error.config?.url)
      ) {
        localStorage.removeItem('auth_token');
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

// Helper function to get auth token
const getAuthToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('auth_token');
};

// Helper function to create axios config from request options
const createAxiosConfig = (options: RequestOptions): AxiosRequestConfig => {
  return {
    url: options.path,
    method: options.method,
    data: options.data,
    headers: options.headers,
    params: options.params,
    timeout: options.timeout,
  };
};

// Helper function to transform axios error to throw
const transformAndThrowError = (error: AxiosError): never => {
  if (error.response?.data && typeof error.response.data === 'object') {
    const responseData = error.response.data as Record<string, unknown>;
    
    // Handle backend errors with 'error' field
    if ('error' in responseData && typeof responseData.error === 'string') {
      throw new ApiError(
        responseData.error,
        error.response.status,
        error.code,
        responseData
      );
    }
    
    // Handle backend errors with 'message' field
    if ('message' in responseData && typeof responseData.message === 'string') {
      throw new ApiError(
        responseData.message,
        error.response.status,
        error.code,
        responseData
      );
    }
  }
  
  // Default error handling
  const errorMessage = error.response?.statusText || error.message || 'Network error occurred';
  throw new ApiError(
    errorMessage,
    error.response?.status,
    error.code,
    {
      status: error.response?.status,
      code: error.code,
    }
  );
};

// Base request function without authentication
export const request = async <T = unknown>(options: RequestOptions): Promise<T> => {
  const axiosInstance = createAxiosInstance();
  const config = createAxiosConfig(options);
  
  try {
    const response = await axiosInstance.request<T>(config);
    return response.data.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      transformAndThrowError(error);
    }
    
    throw new ApiError('Unexpected error occurred', undefined, undefined, error);
  }
};

// Request function with authentication
export const requestWithAuth = async <T = unknown>(options: RequestOptions): Promise<T> => {
  const token = getAuthToken();
  
  if (!token) {
    throw new ApiError('Authentication required', 401);
  }
  
  const optionsWithAuth: RequestOptions = {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
    },
  };
  
  return request<T>(optionsWithAuth);
};
// Public endpoints that personalise when a token is present (category feed / search): send the
// Bearer header if we have one, otherwise fall back to the plain request instead of throwing.
export const requestWithOptionalAuth = async <T = unknown>(options: RequestOptions): Promise<T> => {
  const token = getAuthToken();
  if (!token) return request<T>(options);
  return request<T>({
    ...options,
    headers: { ...options.headers, Authorization: `Bearer ${token}` },
  });
};
